import {
  AccountCircleOutlined,
  AllInboxOutlined,
  ArrowDropDownOutlined,
  BusinessOutlined,
  ChevronRightOutlined,
  DescriptionOutlined,
  TroubleshootOutlined,
  BugReportOutlined,
  Inventory2Outlined,
  LogoutOutlined,
  MenuOutlined,
  ManageAccountsOutlined,
  NotificationsNoneOutlined,
  PrecisionManufacturingOutlined,
  SearchOutlined,
  SettingsOutlined,
  FitScreenOutlined,
  TranslateOutlined,
  HubOutlined,
  AccountTreeOutlined,
  WarehouseOutlined
} from "@mui/icons-material";
import {
  AppBar,
  Alert,
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Typography
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { User } from "oidc-client-ts";
import {
  GlobalSearchResult,
  loadCurrentUser,
  loadGlobalSearch,
  loadOutboundOrders,
  loadTranslationOverrides,
  loadWarehouseStructure,
  submitTerminalDiagnostic
} from "./api";
import { logout } from "./auth";
import { recordSessionPresence } from "./api/access";
import { detectSessionPresence } from "./sessionPresence";
import { WarehouseStructure } from "./WarehouseStructure";
import { ProductMasterData } from "./ProductMasterData";
import { PackagingMasterData } from "./PackagingMasterData";
import { InboundAsnPage } from "./InboundAsnPage";
import { NumberingConfigurationPage } from "./NumberingConfigurationPage";
import { PutawayPage } from "./PutawayPage";
import { QualityInspectionPage } from "./QualityInspectionPage";
import { RepackagingPage } from "./RepackagingPage";
import { SupplierMasterData } from "./SupplierMasterData";
import { StockPage } from "./StockPage";
import { AllocationPage } from "./AllocationPage";
import { OperationalDashboard } from "./OperationalDashboard";
import { ProcessPointMasterData } from "./ProcessPointMasterData";
import { PreparationPage } from "./PreparationPage";
import { ClientDestinationMasterData } from "./ClientDestinationMasterData";
import { DeliveryPage } from "./DeliveryPage";
import {
  CustomerOutboundDashboard,
  OutboundOrderPage
} from "./OutboundOrderPage";
import { AppNavigation, NavigationView } from "./AppNavigation";
import { DocumentDesignerPage } from "./DocumentDesignerPage";
import { HandlingEquipmentPage } from "./HandlingEquipmentPage";
import { HandlingEquipmentMasterDataPage } from "./HandlingEquipmentMasterDataPage";
import { PutawayWorkQueuePage } from "./PutawayWorkQueuePage";
import { InventoryCountPage } from "./InventoryCountPage";
import { AccessManagementPage } from "./AccessManagementPage";
import { FlowDiagnosticPage } from "./FlowDiagnosticPage";
import { TerminalDiagnosticsPage } from "./TerminalDiagnosticsPage";
import { WorkflowDiagnosticPage } from "./WorkflowDiagnosticPage";
import { TranslationManagementPage } from "./TranslationManagementPage";
import { SendDiagnosticDialog } from "./SendDiagnosticDialog";
import { installAutomaticDiagnosticReporter, installDiagnosticCapture,
  setDiagnosticScreen } from "./terminalDiagnostics";
import { applyTranslationOverrides, changeLanguage } from "./i18n";
import { PwaStatus } from "./PwaStatus";
import { SafariInstallButton } from "./SafariInstallButton";
import { StockoutRiskDashboard } from "./StockoutRiskDashboard";
import { ReplenishmentFollowUpDashboard } from "./ReplenishmentFollowUpDashboard";
import { StockoutRiskV2Dashboard } from "./StockoutRiskV2Dashboard";
import { ExpiryRiskDashboard } from "./ExpiryRiskDashboard";
import { operationalStatusLabel } from "./operationalStatus";

interface AppProps {
  oidcUser: User;
}

type AppView = NavigationView;

const navigationViews = new Set<AppView>([
  "home", "suppliers", "inbound", "stock", "allocations", "preparations",
  "warehouse", "products", "packaging", "putaway", "quality", "derogations",
  "repackaging", "configuration", "process-points", "clients", "deliveries",
  "outbound-orders", "documents", "handling-equipment",
  "handling-equipment-masterdata", "putaway-work-queues",
  "inventory-counts", "access-management", "flow-diagnostics", "workflow-diagnostics", "terminal-diagnostics",
  "translations"
]);

function viewFromLocation(): AppView {
  const candidate = new URL(window.location.href).searchParams.get("view");
  return candidate && navigationViews.has(candidate as AppView)
    ? candidate as AppView : "home";
}

const diagnosticScreenKeys: Record<AppView, string> = {
  home: "home", suppliers: "suppliers", inbound: "inbound", stock: "stock",
  allocations: "allocations", preparations: "preparations", warehouse: "warehouse",
  products: "products", packaging: "packaging", putaway: "putaway", quality: "quality",
  derogations: "derogations",
  repackaging: "repackaging", configuration: "configuration", "process-points": "processPoints",
  clients: "clients", deliveries: "deliveries", "outbound-orders": "outboundOrders",
  documents: "documents", "handling-equipment": "handlingEquipment",
  "handling-equipment-masterdata": "handlingEquipmentMasterdata",
  "putaway-work-queues": "putawayWorkQueues", "inventory-counts": "inventoryCounts",
  "access-management": "accessManagement", "flow-diagnostics": "flowDiagnostics",
  "workflow-diagnostics": "workflowDiagnostics",
  "terminal-diagnostics": "terminalDiagnostics", translations: "translations"
};

export function App({ oidcUser }: AppProps) {
  const { t, i18n } = useTranslation();
  const displayScaleStorageKey = `obviowms.display-scale.${oidcUser.profile.sub}`;
  const [displayScale, setDisplayScale] = useState<0.9 | 1>(() =>
    window.localStorage.getItem(displayScaleStorageKey) === "1" ? 1 : 0.9);
  const [view, setView] = useState<AppView>(viewFromLocation);
  const [customerHomeView, setCustomerHomeView] = useState<"requests" | "stock-risk" | "stock-risk-v2" | "replenishment" | "expiry-risk">("requests");
  const [navigationExpanded, setNavigationExpanded] = useState(false);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const [administrationOpen, setAdministrationOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<HTMLElement | null>(null);
  const [sendDiagnosticOpen, setSendDiagnosticOpen] = useState(false);
  const [putawayInitialTab, setPutawayInitialTab] =
    useState<"queues" | "tours" | "tasks" | "alerts" | "closed">("queues");
  const [putawayInitialTaskId, setPutawayInitialTaskId] =
    useState<string | null>(null);
  const [activityOpenDetail, setActivityOpenDetail] = useState(false);
  const [preparationInitialTab, setPreparationInitialTab] =
    useState<"open" | "progress" | "alerts" | "closed">("open");
  const [preparationInitialAllocationId, setPreparationInitialAllocationId] =
    useState<string | null>(null);
  const [preparationInitialWaveId, setPreparationInitialWaveId] =
    useState<string | null>(null);
  const [allocationInitialTargetId, setAllocationInitialTargetId] =
    useState<string | null>(null);
  const [allocationInitialSearch, setAllocationInitialSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [globalTarget, setGlobalTarget] =
    useState<GlobalSearchResult | null>(null);
  const profile = useQuery({
    queryKey: ["current-user"],
    queryFn: loadCurrentUser,
    enabled: Boolean(oidcUser)
  });
  useEffect(() => {
    window.localStorage.setItem(displayScaleStorageKey, String(displayScale));
    document.documentElement.style.zoom = `${displayScale * 100}%`;
    return () => { document.documentElement.style.zoom = "100%"; };
  }, [displayScale, displayScaleStorageKey]);
  const warehouseStructure = useQuery({
    queryKey: ["warehouse-structure", "header"],
    queryFn: loadWarehouseStructure,
    enabled: Boolean(oidcUser)
  });
  const activeWarehouseCode = warehouseStructure.data?.find(
    (node) => node.type === "WAREHOUSE" && node.status === "ACTIVE"
  )?.code ?? "—";
  const translationOverrides = useQuery({
    queryKey: ["translation-overrides"],
    queryFn: loadTranslationOverrides,
    enabled: Boolean(oidcUser),
    staleTime: 60_000
  });
  useEffect(() => {
    if (translationOverrides.data) applyTranslationOverrides(translationOverrides.data);
  }, [translationOverrides.data]);
  useEffect(() => {
    installDiagnosticCapture();
    installAutomaticDiagnosticReporter(async ({ snapshot, source, severity,
      fingerprint, businessContext }) => {
      await submitTerminalDiagnostic({ ...snapshot, source, severity, fingerprint,
        businessContext, comment: "", pagePath: `${location.pathname}${location.search}`,
        pageView: viewFromLocation() });
    });
  }, []);
  useEffect(() => {
    setDiagnosticScreen(t(`transverse.app.screens.${diagnosticScreenKeys[view]}`));
  }, [t, view]);
  useEffect(() => {
    const heartbeat = () => {
      if (document.visibilityState === "visible") {
        void recordSessionPresence(detectSessionPresence()).catch(() => undefined);
      }
    };
    heartbeat();
    const timer = window.setInterval(heartbeat, 60_000);
    document.addEventListener("visibilitychange", heartbeat);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", heartbeat);
    };
  }, [oidcUser.profile.sub]);
  useEffect(() => {
    const url = new URL(window.location.href);
    if (view === "home") url.searchParams.delete("view");
    else url.searchParams.set("view", view);
    window.history.replaceState({}, document.title,
      `${url.pathname}${url.search}${url.hash}`);
  }, [view]);
  useEffect(() => {
    const restoreView = () => setView(viewFromLocation());
    window.addEventListener("popstate", restoreView);
    return () => window.removeEventListener("popstate", restoreView);
  }, []);
  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearch(searchInput.trim()),
      250
    );
    return () => window.clearTimeout(timer);
  }, [searchInput]);
  const globalSearch = useQuery({
    queryKey: ["global-search", debouncedSearch],
    queryFn: () => loadGlobalSearch(debouncedSearch),
    enabled: Boolean(oidcUser) && debouncedSearch.length >= 2,
    staleTime: 15_000
  });
  const goHome = useCallback(() => {
    setView("home");
    setGlobalTarget(null);
    setPutawayInitialTab("queues");
    setPutawayInitialTaskId(null);
    setPreparationInitialTab("open");
    setPreparationInitialAllocationId(null);
    setPreparationInitialWaveId(null);
    setAllocationInitialTargetId(null);
    setAllocationInitialSearch("");
    window.history.replaceState({}, document.title, "/");
  }, []);
  const openAdministrationView = (nextView: AppView) => {
    setAdministrationOpen(false);
    setGlobalTarget(null);
    setView(nextView);
  };
  const openSearchResult = (result: GlobalSearchResult) => {
    setGlobalTarget(result);
    setView(result.view);
    setSearchInput("");
    setDebouncedSearch("");
  };
  const navigateFromMenu = (nextView: NavigationView) => {
    setGlobalTarget(null);
    setPutawayInitialTab("queues");
    setPutawayInitialTaskId(null);
    setPreparationInitialTab("open");
    setPreparationInitialAllocationId(null);
    setPreparationInitialWaveId(null);
    setAllocationInitialTargetId(null);
    setAllocationInitialSearch("");
    setView(nextView);
  };
  const permissions = profile.data?.permissions ?? [];
  const can = (permission: string) => permissions.includes(permission);
  const customerPortal = Boolean(
    oidcUser
    && profile.data
    && (permissions.includes("outbound_order:self:read")
      || permissions.includes("derogation:self:read"))
    && !permissions.includes("outbound_order:read")
  );
  const outboundNotifications = useQuery({
    queryKey: ["outbound-orders", "internal"],
    queryFn: loadOutboundOrders,
    enabled: Boolean(oidcUser) && !customerPortal
      && permissions.includes("outbound_order:read"),
    refetchInterval: 60_000
  });
  const pendingOutboundCount = (outboundNotifications.data ?? []).filter(
    (order) => order.status === "SUBMITTED"
  ).length;

  return (
    <Box sx={{
      // CSS zoom reduces the rendered 100dvh as well. Compensate the app shell
      // so compact display still occupies the complete physical viewport.
      height: `calc(100dvh / ${displayScale})`, minHeight: 0,
      bgcolor: "background.default",
      display: "flex", flexDirection: "column", overflow: "hidden"
    }}>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ minHeight: { xs: 64, md: 72 }, gap: 1.5 }}>
          {oidcUser && !customerPortal && (
            <IconButton color="inherit" aria-label="Ouvrir la navigation"
              onClick={() => setMobileNavigationOpen(true)}
              sx={{ display: { xs: "inline-flex", md: "none" }, ml: -.75 }}>
              <MenuOutlined />
            </IconButton>
          )}
          <Box component="img" src="/brand/obviowms-header.svg"
            alt="ObvioWMS — Simply Obvious"
            sx={{
              width: { xs: 150, sm: 184 },
              height: { xs: 44, sm: 52 },
              objectFit: "contain",
              objectPosition: "left center",
              flexShrink: 0
            }} />
          {oidcUser && customerPortal && <>
            <Divider orientation="vertical" flexItem sx={{
              my: 1.5, borderColor: "rgba(255,255,255,.28)"
            }} />
            <Typography component="h1" variant="h6" fontWeight={800}
              sx={{ color: "inherit", whiteSpace: "nowrap",
                display: { xs: "none", sm: "block" } }}>
              {t("outboundOrder.portal.cockpit.appBarTitle")}
            </Typography>
          </>}
          {oidcUser && !customerPortal && (
            <Button color="inherit" variant="outlined" startIcon={<WarehouseOutlined />}
              endIcon={<ArrowDropDownOutlined />}
              sx={{
                ml: { xs: "auto", sm: 1 },
                borderColor: "rgba(255,255,255,.3)",
                display: { xs: "none", sm: "inline-flex" }
              }}>
              {activeWarehouseCode}
            </Button>
          )}
          {oidcUser && !customerPortal && (
            <Autocomplete<GlobalSearchResult, false, false, true>
              freeSolo
              options={globalSearch.data ?? []}
              inputValue={searchInput}
              value={null}
              filterOptions={(options) => options}
              loading={globalSearch.isFetching}
              groupBy={(option) => ({
                ASN: "ASN",
                RECEIPT: t("transverse.app.groups.RECEIPT"),
                HANDLING_UNIT: t("transverse.app.groups.HANDLING_UNIT"),
                PRODUCT: t("transverse.app.groups.PRODUCT"),
                LOCATION: t("transverse.app.groups.LOCATION"),
                OUTBOUND_ORDER: t("transverse.app.groups.OUTBOUND_ORDER")
              })[option.type]}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.reference}
              isOptionEqualToValue={(option, value) =>
                option.type === value.type && option.id === value.id}
              onInputChange={(_, value) => setSearchInput(value)}
              onChange={(_, value) => {
                if (value && typeof value !== "string") openSearchResult(value);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && globalSearch.data?.length === 1) {
                  event.preventDefault();
                  openSearchResult(globalSearch.data[0]);
                }
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={`${option.type}-${option.id}`}
                  sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="body2" fontWeight={800}>
                      {option.reference} · {option.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.context}
                    </Typography>
                  </Box>
                  <Chip label={operationalStatusLabel(option.status)} size="small" variant="outlined" />
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} size="small"
                  placeholder={t("transverse.app.search")}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      startAdornment: <InputAdornment position="start">
                        <SearchOutlined sx={{ color: "rgba(255,255,255,.72)" }} />
                      </InputAdornment>,
                      endAdornment: <>
                        {globalSearch.isFetching
                          ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    }
                  }} />
              )}
              sx={{
                flex: 1, maxWidth: 1560, display: { xs: "none", md: "block" },
                "& .MuiOutlinedInput-root": {
                  color: "white", bgcolor: "rgba(255,255,255,.06)",
                  "& fieldset": { borderColor: "rgba(255,255,255,.28)" }
                },
                "& input::placeholder": { color: "rgba(255,255,255,.62)", opacity: 1 }
              }} />
          )}
          <Box sx={{
            flexGrow: oidcUser ? { xs: 1, md: customerPortal ? 1 : 0 } : 1
          }} />
          {oidcUser ? (
            <>
              <SafariInstallButton />
              {!customerPortal && <IconButton color="inherit"
                aria-label={t("transverse.app.manageData")}
                data-testid="open-administration"
                onClick={() => setAdministrationOpen(true)}>
                <SettingsOutlined />
              </IconButton>}
              {!customerPortal && <IconButton color="inherit"
                aria-label={t("transverse.app.pendingRequests", { count: pendingOutboundCount })}
                onClick={() => navigateFromMenu("outbound-orders")}
                sx={{ display: { xs: "none", sm: "inline-flex" } }}>
                <Badge badgeContent={pendingOutboundCount} color="error"
                  max={99} invisible={pendingOutboundCount === 0}>
                  <NotificationsNoneOutlined />
                </Badge>
              </IconButton>}
              <Button color="inherit" startIcon={<AccountCircleOutlined />}
                endIcon={<ArrowDropDownOutlined />}
                aria-controls={userMenuAnchor ? "user-session-menu" : undefined}
                aria-haspopup="menu" aria-expanded={Boolean(userMenuAnchor)}
                onClick={(event) => setUserMenuAnchor(event.currentTarget)}>
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  {profile.data?.displayName ?? oidcUser.profile.name ?? t("transverse.app.user")}
                </Box>
              </Button>
              <Menu id="user-session-menu" anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={() => {
                  setLanguageMenuAnchor(null);
                  setUserMenuAnchor(null);
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}>
                <Box sx={{ px: 2, py: 1, minWidth: 240 }}>
                  <Typography fontWeight={800}>
                    {profile.data?.displayName ?? oidcUser.profile.name ?? t("transverse.app.user")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {oidcUser.profile.email ?? oidcUser.profile.preferred_username}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem aria-haspopup="menu"
                  aria-controls={languageMenuAnchor ? "language-menu" : undefined}
                  aria-expanded={Boolean(languageMenuAnchor)}
                  onClick={(event) => setLanguageMenuAnchor(event.currentTarget)}>
                  <ListItemIcon><TranslateOutlined fontSize="small" /></ListItemIcon>
                  <ListItemText>{t("common.language.menu")}</ListItemText>
                  <ChevronRightOutlined fontSize="small" />
                </MenuItem>
                <MenuItem selected={displayScale === 0.9} onClick={() => {
                  setDisplayScale(current => current === 0.9 ? 1 : 0.9);
                  setUserMenuAnchor(null);
                }}>
                  <ListItemIcon><FitScreenOutlined fontSize="small" /></ListItemIcon>
                  <ListItemText primary={t("transverse.app.compactDisplay")}
                    secondary={displayScale === 0.9 ? "90 %" : "100 %"} />
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => {
                  setUserMenuAnchor(null);
                  setSendDiagnosticOpen(true);
                }}>
                  <ListItemIcon><BugReportOutlined fontSize="small" /></ListItemIcon>
                  <ListItemText>{t("transverse.app.sendDiagnostic")}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => {
                  setUserMenuAnchor(null);
                  void logout();
                }}>
                  <ListItemIcon><LogoutOutlined fontSize="small" /></ListItemIcon>
                  <ListItemText>{t("transverse.app.logout")}</ListItemText>
                </MenuItem>
              </Menu>
              <Menu id="language-menu" anchorEl={languageMenuAnchor}
                open={Boolean(languageMenuAnchor)}
                onClose={() => setLanguageMenuAnchor(null)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}>
                <MenuItem selected={i18n.resolvedLanguage === "fr"}
                  onClick={() => {
                    setLanguageMenuAnchor(null);
                    setUserMenuAnchor(null);
                    void changeLanguage("fr");
                  }}>
                  {t("common.language.french")}
                </MenuItem>
                <MenuItem selected={i18n.resolvedLanguage === "en"}
                  onClick={() => {
                    setLanguageMenuAnchor(null);
                    setUserMenuAnchor(null);
                    void changeLanguage("en");
                  }}>
                  {t("common.language.english")}
                </MenuItem>
              </Menu>
            </>
          ) : null}
        </Toolbar>
      </AppBar>
      <PwaStatus />

      <Box sx={{ display: "flex", flex: 1, minHeight: 0, minWidth: 0 }}>
      {oidcUser && !customerPortal && !profile.isLoading && (
        <AppNavigation permissions={permissions} currentView={view}
          expanded={navigationExpanded}
          mobileOpen={mobileNavigationOpen}
          onToggle={() => setNavigationExpanded((expanded) => !expanded)}
          onMobileClose={() => setMobileNavigationOpen(false)}
          onNavigate={navigateFromMenu} />
      )}
      <Box component="main"
        sx={{
          flex: 1, width: 0,
          maxWidth: customerPortal ? 1536 : "none",
          mx: customerPortal ? "auto" : 0,
          minWidth: 0, height: "100%", overflow: "auto",
          px: customerPortal ? { xs: 2, sm: 3 } : { xs: 1.5, md: 2 },
          py: {
            xs: 2.5,
            md: 1
          },
          ...((customerPortal || view === "home") && oidcUser ? {
            height: { md: "100%" },
            minHeight: 0,
            overflow: { xs: "auto", md: "hidden" },
            display: { md: "flex" }
          } : {})
        }}>
        {oidcUser && profile.isLoading ? (
          <Stack alignItems="center" justifyContent="center"
            sx={{ width: "100%", minHeight: 240 }}>
            <CircularProgress />
          </Stack>
        ) : profile.isError ? (
          <Stack alignItems="center" justifyContent="center" spacing={2}
            sx={{ width: "100%", minHeight: 300 }}>
            <Alert severity="error" sx={{ maxWidth: 640, width: "100%" }}>
              {t("transverse.app.profileError")}
              <Typography component="div" variant="caption" sx={{ mt: 1 }}>
                {t("transverse.app.detail")} : {profile.error instanceof Error
                  ? profile.error.message : String(profile.error)}
              </Typography>
            </Alert>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button variant="contained" onClick={() => void profile.refetch()}>
                {t("common.actions.retry")}
              </Button>
              <Button variant="outlined" onClick={() => void logout()}>
                {t("transverse.app.logout")}
              </Button>
            </Stack>
          </Stack>
        ) : customerPortal ? (
          customerHomeView === "replenishment" && can("replenishment:read")
            ? <ReplenishmentFollowUpDashboard canExecute={can("replenishment:execute")}
                operationsLabel={t("outboundOrder.portal.cockpit.appBarTitle")}
                showStockRisk={false}
                showStockRiskV2={can("stock_risk_v2:read")}
                showExpiryRisk={can("expiry_risk:read")}
                onChangeView={(next) => setCustomerHomeView(
                  next === "operations" ? "requests" : next)} />
          : customerHomeView === "stock-risk" && can("stock_risk:read")
            ? <StockoutRiskDashboard canRead canReviewMaterials={false}
                operationsLabel={t("outboundOrder.portal.cockpit.appBarTitle")}
                onShowOperations={() => setCustomerHomeView("requests")}
                onShowReplenishment={() => setCustomerHomeView("replenishment")}
                onShowStockRiskV2={() => setCustomerHomeView("stock-risk-v2")}
                onReviewMaterials={() => undefined} />
          : customerHomeView === "stock-risk-v2" && can("stock_risk_v2:read")
            ? <StockoutRiskV2Dashboard canRead canOpenMaterial={false}
                operationsLabel={t("outboundOrder.portal.cockpit.appBarTitle")}
                showReplenishment={can("replenishment:read")}
                showExpiryRisk={can("expiry_risk:read")}
                onChangeView={(next) => setCustomerHomeView(next === "operations" ? "requests" : next)}
                onOpenMaterial={() => undefined} />
          : customerHomeView === "expiry-risk" && can("expiry_risk:read")
            ? <ExpiryRiskDashboard canRead canApprove={can("derogation:execute")}
                operationsLabel={t("outboundOrder.portal.cockpit.appBarTitle")}
                showStockRiskV2={can("stock_risk_v2:read")}
                showReplenishment={can("replenishment:read")}
                onChangeView={(next) => setCustomerHomeView(next === "operations" ? "requests" : next)} />
            : <CustomerOutboundDashboard permissions={permissions}
                onShowStockRiskV2={() => setCustomerHomeView("stock-risk-v2")}
                onShowReplenishment={() => setCustomerHomeView("replenishment")} />
        ) : view === "warehouse" && oidcUser ? (
          <WarehouseStructure onBack={goHome}
            initialBinId={globalTarget?.view === "warehouse"
              ? globalTarget.id : null} />
        ) : view === "products" && oidcUser ? (
          <ProductMasterData onBack={goHome}
            permissions={profile.data?.permissions ?? []}
            initialSearch={globalTarget?.view === "products"
              ? globalTarget.searchTerm : ""}
            initialTargetId={globalTarget?.view === "products"
              ? globalTarget.id : null} />
        ) : view === "packaging" && oidcUser ? (
          <PackagingMasterData onBack={goHome}
            permissions={profile.data?.permissions ?? []} />
        ) : view === "inbound" && oidcUser ? (
          <InboundAsnPage onBack={goHome}
            initialSearch={globalTarget?.view === "inbound"
              ? globalTarget.searchTerm : ""}
            initialTargetId={putawayInitialTaskId}
            initialOpenDetail={activityOpenDetail} />
        ) : view === "stock" && oidcUser ? (
          <StockPage onBack={goHome}
            permissions={profile.data?.permissions ?? []}
            initialSearch={globalTarget?.view === "stock"
              ? globalTarget.searchTerm : ""}
            initialView={globalTarget?.type === "HANDLING_UNIT"
              ? "movement" : "item"} />
        ) : view === "inventory-counts" && oidcUser ? (
          <InventoryCountPage onBack={goHome}
            canManage={permissions.includes("inventory_count:manage")}
            canSupervise={permissions.includes("inventory_count:supervise")}
            canAdjust={permissions.includes("inventory_count:adjust")}
            canApprove={permissions.includes("inventory_count:approve")}
            canCancel={permissions.includes("inventory_count:cancel")} />
        ) : view === "allocations" && oidcUser ? (
          <AllocationPage onBack={goHome}
            initialTargetId={allocationInitialTargetId}
            initialOpenDetail={activityOpenDetail}
            initialSearch={allocationInitialSearch}
            onPrepare={(allocationId) => {
              setPreparationInitialAllocationId(allocationId);
              setPreparationInitialTab("open");
              setPutawayInitialTaskId(null);
              setView("preparations");
            }}
            permissions={profile.data?.permissions ?? []} />
        ) : view === "preparations" && oidcUser ? (
          <PreparationPage onBack={goHome}
            initialTab={preparationInitialTab}
            initialTaskId={putawayInitialTaskId}
            initialWaveId={preparationInitialWaveId}
            initialAllocationId={preparationInitialAllocationId}
            initialOpenDetail={activityOpenDetail}
            onPreparationCreated={() => {
              if (preparationInitialAllocationId) {
                setPreparationInitialAllocationId(null);
                setAllocationInitialTargetId(null);
                setAllocationInitialSearch("");
                setView("allocations");
              }
            }}
            permissions={profile.data?.permissions ?? []} />
        ) : view === "deliveries" && oidcUser ? (
          <DeliveryPage onBack={goHome}
            initialMissionId={putawayInitialTaskId}
            initialOpenDetail={activityOpenDetail}
            permissions={profile.data?.permissions ?? []} />
        ) : view === "outbound-orders" && oidcUser ? (
          <OutboundOrderPage onBack={goHome}
            permissions={profile.data?.permissions ?? []}
            initialSearch={globalTarget?.view === "outbound-orders"
              ? globalTarget.searchTerm : ""}
            initialTargetId={globalTarget?.view === "outbound-orders"
              ? globalTarget.id : null}
            onOpenReference={(reference) => {
              setGlobalTarget(null);
              setPreparationInitialAllocationId(null);
              setPreparationInitialWaveId(null);
              if (reference.type === "ALLOCATION") {
                setAllocationInitialTargetId(reference.id);
                setAllocationInitialSearch("");
                setView("allocations");
              } else if (reference.type === "ALLOCATION_GROUP") {
                setAllocationInitialTargetId(null);
                setAllocationInitialSearch(reference.label);
                setView("allocations");
              } else if (reference.type === "PREPARATION_TASK") {
                setPutawayInitialTaskId(reference.id);
                setPreparationInitialTab("open");
                setView("preparations");
              } else if (reference.type === "PREPARATION_WAVE") {
                setPutawayInitialTaskId(null);
                setPreparationInitialWaveId(reference.id);
                setView("preparations");
              } else if (reference.type === "DELIVERY_MISSION") {
                setPutawayInitialTaskId(reference.id);
                setView("deliveries");
              }
            }} />
        ) : view === "suppliers" && oidcUser ? (
          <SupplierMasterData onBack={goHome}
            permissions={profile.data?.permissions ?? []} />
        ) : view === "configuration" && oidcUser ? (
          <NumberingConfigurationPage onBack={goHome}
            canConfigureReplenishment={can("replenishment:configure")} />
        ) : view === "handling-equipment" && oidcUser ? (
          <HandlingEquipmentPage onBack={goHome} permissions={permissions} />
        ) : view === "handling-equipment-masterdata" && oidcUser ? (
          <HandlingEquipmentMasterDataPage onBack={goHome} />
        ) : view === "putaway-work-queues" && oidcUser ? (
          <PutawayWorkQueuePage onBack={goHome} />
        ) : view === "documents" && oidcUser ? (
          <DocumentDesignerPage onBack={goHome}
            canManage={permissions.includes("document_template:manage")} />
        ) : view === "access-management" && oidcUser ? (
          <AccessManagementPage onBack={goHome}
            permissions={profile.data?.permissions ?? []} />
        ) : view === "flow-diagnostics" && oidcUser
            && can("flow_diagnostic:read") ? (
          <FlowDiagnosticPage onBack={goHome} />
        ) : view === "workflow-diagnostics" && oidcUser
            && can("workflow_diagnostic:read") ? (
          <WorkflowDiagnosticPage onBack={goHome} />
        ) : view === "terminal-diagnostics" && oidcUser
            && can("terminal_diagnostic:read") ? (
          <TerminalDiagnosticsPage onBack={goHome} />
        ) : view === "translations" && oidcUser
            && can("translation:manage") ? (
          <TranslationManagementPage onBack={goHome} />
        ) : view === "process-points" && oidcUser ? (
          <ProcessPointMasterData onBack={goHome} />
        ) : view === "clients" && oidcUser ? (
          <ClientDestinationMasterData onBack={goHome}
            permissions={profile.data?.permissions ?? []} />
        ) : view === "putaway" && oidcUser ? (
          <PutawayPage onBack={goHome} initialTab={putawayInitialTab}
            initialTaskId={putawayInitialTaskId}
            initialOpenDetail={activityOpenDetail}
            onOpenRepackaging={(taskId) => {
              setPutawayInitialTaskId(taskId);
              setView("repackaging");
            }} />
        ) : view === "quality" && oidcUser ? (
          <QualityInspectionPage onBack={goHome}
            initialTaskId={putawayInitialTaskId}
            initialOpenDetail={activityOpenDetail}
            permissions={profile.data?.permissions ?? []} />
        ) : view === "derogations" && oidcUser ? (
          <QualityInspectionPage onBack={goHome} mode="derogations"
            permissions={profile.data?.permissions ?? []} />
        ) : view === "repackaging" && oidcUser ? (
          <RepackagingPage onBack={goHome}
            initialTaskId={putawayInitialTaskId}
            initialOpenDetail={activityOpenDetail}
            permissions={profile.data?.permissions ?? []} />
        ) : oidcUser ? (
          <OperationalDashboard profile={profile.data}
            onOpenMaterial={(itemId) => {
              setGlobalTarget({
                type: "PRODUCT", id: itemId, reference: "", title: "",
                context: "", status: "", view: "products", searchTerm: ""
              });
              setView("products");
            }}
            onNavigate={(nextView, targetTab, taskId, openDetail = false) => {
              setGlobalTarget(null);
              if (nextView === "preparations") {
                setPreparationInitialAllocationId(null);
                setPreparationInitialTab(
                  targetTab === "progress" ? "progress" : "open"
                );
              } else {
                setPutawayInitialTab(
                  targetTab === "tasks" || targetTab === "alerts"
                    || targetTab === "closed" || targetTab === "tours"
                    || targetTab === "queues" ? targetTab : "queues"
                );
              }
              setPutawayInitialTaskId(taskId ?? null);
              setActivityOpenDetail(openDetail);
              if (nextView === "allocations") {
                setAllocationInitialTargetId(taskId ?? null);
              } else {
                setAllocationInitialTargetId(null);
              }
              setView(nextView);
            }} />
        ) : null}
      </Box>
      </Box>

      <Dialog open={administrationOpen}
        onClose={() => setAdministrationOpen(false)}
        fullWidth maxWidth="md">
        <DialogTitle>{t("administration.title")}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t("administration.description")}
          </Typography>
          <Grid container spacing={1.5}>
            {[
              {
                label: t("administration.entries.products"), view: "products" as AppView,
                permission: "product:read", icon: <Inventory2Outlined />
              },
              {
                label: t("administration.entries.suppliers"), view: "suppliers" as AppView,
                permission: "supplier:read", icon: <BusinessOutlined />
              },
              {
                label: t("administration.entries.packaging"), view: "packaging" as AppView,
                permission: "packaging:read", icon: <AllInboxOutlined />
              },
              {
                label: t("administration.entries.warehouse"), view: "warehouse" as AppView,
                permission: "warehouse:read", icon: <WarehouseOutlined />
              },
              {
                label: t("administration.entries.processPoints"), view: "process-points" as AppView,
                permission: "warehouse:read", icon: <HubOutlined />
              },
              {
                label: t("administration.entries.clients"), view: "clients" as AppView,
                permission: "client:read", icon: <BusinessOutlined />
              },
              {
                label: t("administration.entries.equipment"), view: "handling-equipment-masterdata" as AppView,
                permission: "warehouse:manage", icon: <PrecisionManufacturingOutlined />
              },
              {
                label: t("administration.entries.putawayQueues"), view: "putaway-work-queues" as AppView,
                permission: "putaway_task:read", icon: <AllInboxOutlined />
              },
              {
                label: t("administration.entries.configuration"), view: "configuration" as AppView,
                permission: "numbering:read", icon: <SettingsOutlined />
              },
              {
                label: t("administration.entries.documents"), view: "documents" as AppView,
                permission: "document_template:read", icon: <DescriptionOutlined />
              },
              {
                label: t("administration.entries.access"),
                view: "access-management" as AppView,
                permission: "role:read", icon: <ManageAccountsOutlined />
              },
              {
                label: t("administration.entries.flowDiagnostics"),
                view: "flow-diagnostics" as AppView,
                permission: "flow_diagnostic:read", icon: <TroubleshootOutlined />
              },
              {
                label: t("administration.entries.workflowDiagnostics"),
                view: "workflow-diagnostics" as AppView,
                permission: "workflow_diagnostic:read", icon: <AccountTreeOutlined />
              },
              {
                label: t("administration.entries.terminalDiagnostics"),
                view: "terminal-diagnostics" as AppView,
                permission: "terminal_diagnostic:read", icon: <BugReportOutlined />
              },
              {
                label: t("administration.entries.translations"),
                view: "translations" as AppView,
                permission: "translation:manage", icon: <TranslateOutlined />
              }
            ].filter((item) => can(item.permission)).map((item) => (
              <Grid key={item.label} size={{ xs: 12, sm: 6, md: 4 }}>
                <Button fullWidth variant="outlined"
                  data-testid={`administration-${item.view}`}
                  startIcon={item.icon}
                  onClick={() => openAdministrationView(item.view)}
                  sx={{
                    minHeight: 88, flexDirection: "column", gap: 1,
                    fontWeight: 800
                  }}>
                  {item.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
      <SendDiagnosticDialog open={sendDiagnosticOpen} pageView={view}
        onClose={() => setSendDiagnosticOpen(false)} />
    </Box>
  );
}
